import { RRule, Weekday, ByWeekday } from 'rrule'
import moment from 'moment'
import { t } from 'i18next'
import * as dateUtils from 'pages/calendar/utils/DateUtils'
import { convertToRRuleOptions } from './rruleCompat'

/**
 * RRule options interface
 */
interface RRuleOptions {
  freq: number
  interval?: number
  until?: Date
  count?: number
  byweekday?: ByWeekday[] | Weekday[] | number[]
  bymonthday?: number | number[]
  bymonth?: number | number[]
  bysetpos?: number | number[]
}

/**
 * RRule with original options
 */
export interface RRuleWithOptions extends RRule {
  origOptions: RRuleOptions
}

/**
 * Generate a human-readable description of an RRule
 */
export const rruleDescription = (rrule: RRuleWithOptions | null | undefined): string => {
  if (!rrule) {
    return ''
  }

  // node-ical(rrule-temporal)에서 파싱된 RRule은 origOptions가 camelCase 키 + string freq라
  // 아래 분기(numeric RRule.WEEKLY 비교, byweekday 키 등)에 맞지 않아 빈 문자열이 반환된다.
  const options = convertToRRuleOptions(
    rrule.origOptions as unknown as Record<string, unknown>,
  ) as RRuleOptions

  const freq = options.freq
  const interval = options.interval || 1

  if (freq === RRule.DAILY) {
    return _descDaily(interval, options.until, options.count)
  }

  if (freq === RRule.WEEKLY) {
    return _descWeekly(interval, options.byweekday, options.until, options.count)
  }

  if (freq === RRule.MONTHLY) {
    if (options.byweekday) {
      return _descMonthlyNth(
        interval,
        options.byweekday,
        options.bysetpos || 1,
        options.until,
        options.count,
      )
    }
    return _descMonthly(interval, options.bymonthday, options.until, options.count)
  }

  if (freq === RRule.YEARLY) {
    if (options.byweekday) {
      return _descYearlyNth(
        interval,
        options.byweekday,
        options.bysetpos || 1,
        options.bymonth,
        options.until,
        options.count,
      )
    }

    return _descYearly(interval, options.bymonth, options.bymonthday, options.until, options.count)
  }

  return ''
}

/**
 * Generate description for daily recurrence
 */
const _descDaily = (interval: number, until?: Date, count?: number): string => {
  const untilStr = _formatUntilDate(until)
  const hasUntil = !!untilStr
  const isEvery = interval === 1

  if (isEvery && hasUntil) {
    return t('calendar.recurrence.dailyDesc.until', { until: untilStr })
  }
  if (isEvery && !hasUntil) {
    if (!count || count === 0) {
      return t('calendar.recurrence.dailyDesc.endless')
    }
    if (count === 1) {
      return t('calendar.recurrence.dailyDesc.count1')
    }
    return t('calendar.recurrence.dailyDesc.count', { count })
  }

  if (!isEvery && hasUntil) {
    return t('calendar.recurrence.dailyDesc.intervalUntil', {
      interval,
      untilStr,
    })
  }

  if (!isEvery && !hasUntil) {
    if (!count || count === 0) {
      return t('calendar.recurrence.dailyDesc.intervalEndless', { interval })
    }
    if (count === 1) {
      return t('calendar.recurrence.dailyDesc.intervalCount1', {
        interval,
      })
    }
    return t('calendar.recurrence.dailyDesc.intervalCount', {
      interval,
      count,
    })
  }

  return ''
}

/**
 * Get text representation of weekdays
 */
const _getWeekdayText = (
  byweekday: ByWeekday[] | Weekday[] | number[] | undefined,
): string | null => {
  if (!byweekday || byweekday.length === 0) {
    return null
  }
  const weekdayNames = moment.localeData().weekdaysShort()
  return byweekday
    .map((day: any) => {
      const weekday = typeof day === 'number' ? day : day.weekday
      return weekdayNames[(weekday + 1) % 7]
    })
    .join(', ')
}

/**
 * Format until date as localized string
 */
const _formatUntilDate = (until?: Date): string | null => {
  if (!until) return null

  return dateUtils.localizedDateString(until)
}

/**
 * Generate description for weekly recurrence
 */
const _descWeekly = (
  interval: number,
  byweekday: ByWeekday[] | Weekday[] | number[] | undefined,
  until?: Date,
  count?: number,
): string => {
  const untilStr = _formatUntilDate(until)
  const hasUntil = !!untilStr
  const isEvery = interval === 1
  const weekdaysStr = _getWeekdayText(byweekday)

  if (isEvery && hasUntil) {
    return t('calendar.recurrence.weeklyDesc.until', {
      weekdays: weekdaysStr,
      until: untilStr,
    })
  }

  if (isEvery && !hasUntil) {
    if (!count || count === 0) {
      return t('calendar.recurrence.weeklyDesc.endless', {
        weekdays: weekdaysStr,
      })
    }
    if (count === 1) {
      return t('calendar.recurrence.weeklyDesc.count1', {
        weekdays: weekdaysStr,
      })
    }
    return t('calendar.recurrence.weeklyDesc.count', {
      weekdays: weekdaysStr,
      count,
    })
  }

  if (!isEvery && hasUntil) {
    return t('calendar.recurrence.weeklyDesc.intervalUntil', {
      interval,
      weekdays: weekdaysStr,
      untilStr,
    })
  }

  if (!isEvery && !hasUntil) {
    if (!count || count === 0) {
      return t('calendar.recurrence.weeklyDesc.intervalEndless', {
        interval,
        weekdays: weekdaysStr,
      })
    }
    if (count === 1) {
      return t('calendar.recurrence.weeklyDesc.intervalCount1', {
        interval,
        weekdays: weekdaysStr,
      })
    }
    return t('calendar.recurrence.weeklyDesc.intervalCount', {
      interval,
      weekdays: weekdaysStr,
      count,
    })
  }

  return ''
}

/**
 * Generate description for monthly recurrence (by day of month)
 */
const _descMonthly = (
  interval: number,
  bymonthday: number | number[] | undefined,
  until?: Date,
  count?: number,
): string => {
  const untilStr = _formatUntilDate(until)
  const hasUntil = !!untilStr
  const isEvery = interval === 1
  let monthdayArr = bymonthday
  if (bymonthday && !Array.isArray(bymonthday)) {
    monthdayArr = [bymonthday]
  }
  const day = monthdayArr ? (monthdayArr as number[])[0] : null

  if (!day) return ''

  if (isEvery && hasUntil) {
    return t('calendar.recurrence.monthlyDesc.until', {
      day,
      until: untilStr,
    })
  }

  if (isEvery && !hasUntil) {
    if (!count || count === 0) {
      return t('calendar.recurrence.monthlyDesc.endless', {
        day,
      })
    }
    if (count === 1) {
      return t('calendar.recurrence.monthlyDesc.count1', {
        day,
      })
    }
    return t('calendar.recurrence.monthlyDesc.count', {
      day,
      count,
    })
  }

  if (!isEvery && hasUntil) {
    return t('calendar.recurrence.monthlyDesc.intervalUntil', {
      interval,
      day,
      untilStr,
    })
  }

  if (!isEvery && !hasUntil) {
    if (!count || count === 0) {
      return t('calendar.recurrence.monthlyDesc.intervalEndless', {
        interval,
        day,
      })
    }
    if (count === 1) {
      return t('calendar.recurrence.monthlyDesc.intervalCount1', {
        interval,
        day,
      })
    }
    return t('calendar.recurrence.monthlyDesc.intervalCount', {
      interval,
      day,
      count,
    })
  }

  return ''
}

/**
 * Get text representation of nth position (e.g., "first", "last")
 */
const _getNthText = (bysetpos: number | number[] | undefined): string => {
  let setposArr = bysetpos
  if (!bysetpos || !Array.isArray(bysetpos)) {
    setposArr = [bysetpos as number]
  }
  if (!setposArr || (setposArr as number[]).length === 0) return ''

  const pos = (setposArr as number[])[0]

  switch (pos) {
    case 1:
      return t('calendar.recurrence.nthFirst')
    case 2:
      return t('calendar.recurrence.nthSecond')
    case 3:
      return t('calendar.recurrence.nthThird')
    case 4:
      return t('calendar.recurrence.nthFourth')
    case -1:
      return t('calendar.recurrence.nthLast')
    default:
      return ''
  }
}

/**
 * Get text representation of nth weekday (e.g., "Monday", "weekday", "day")
 */
const _nthWeekDayText = (byweekday: ByWeekday[] | Weekday[] | number[] | undefined): string => {
  if (!byweekday || byweekday.length === 0) return ''

  let weekdayArr = byweekday
  if (!Array.isArray(byweekday)) {
    weekdayArr = [byweekday]
  }

  const weekdays = (weekdayArr as any[]).map((day) => (typeof day === 'number' ? day : day.weekday))

  if (weekdays.length === 7) {
    return t('calendar.recurrence.nthDay')
  }
  if (weekdays.length === 5 && !weekdays.includes(5) && !weekdays.includes(6)) {
    return t('calendar.recurrence.nthWeekday')
  }
  if (weekdays.length === 2 && weekdays.includes(5) && weekdays.includes(6)) {
    return t('calendar.recurrence.nthWeekend')
  }

  return _getWeekdayText(byweekday) || ''
}

/**
 * Generate description for monthly recurrence (by nth weekday)
 */
const _descMonthlyNth = (
  interval: number,
  byweekday: ByWeekday[] | Weekday[] | number[] | undefined,
  bysetpos: number | number[] | undefined,
  until?: Date,
  count?: number,
): string => {
  const untilStr = _formatUntilDate(until)
  const hasUntil = !!untilStr
  const isEvery = interval === 1

  const nth = _getNthText(bysetpos)
  const weekday = _nthWeekDayText(byweekday)

  if (!nth || !weekday) return ''

  if (isEvery && hasUntil) {
    return t('calendar.recurrence.monthlyNthDesc.until', {
      nth,
      weekday,
      until: untilStr,
    })
  }

  if (isEvery && !hasUntil) {
    if (!count || count === 0) {
      return t('calendar.recurrence.monthlyNthDesc.endless', {
        nth,
        weekday,
      })
    }
    if (count === 1) {
      return t('calendar.recurrence.monthlyNthDesc.count1', {
        nth,
        weekday,
      })
    }
    return t('calendar.recurrence.monthlyNthDesc.count', {
      nth,
      weekday,
      count,
    })
  }

  if (!isEvery && hasUntil) {
    return t('calendar.recurrence.monthlyNthDesc.intervalUntil', {
      nth,
      weekday,
      interval,
      untilStr,
    })
  }

  if (!isEvery && !hasUntil) {
    if (!count || count === 0) {
      return t('calendar.recurrence.monthlyNthDesc.intervalEndless', {
        nth,
        weekday,
        interval,
      })
    }
    if (count === 1) {
      return t('calendar.recurrence.monthlyNthDesc.intervalCount1', {
        nth,
        weekday,
        interval,
      })
    }
    return t('calendar.recurrence.monthlyNthDesc.intervalCount', {
      nth,
      weekday,
      interval,
      count,
    })
  }

  return ''
}

/**
 * Format month and day as localized string
 */
const _formatMonthDay = (month: number | undefined, day: number | undefined): string => {
  if (!month || !day) return ''

  const date = moment()
    .month(month - 1)
    .date(day)
  return date.format('MMM Do')
}

/**
 * Generate description for yearly recurrence (by date)
 */
const _descYearly = (
  interval: number,
  bymonth: number | number[] | undefined,
  bymonthday: number | number[] | undefined,
  until?: Date,
  count?: number,
): string => {
  const untilStr = _formatUntilDate(until)
  const hasUntil = !!untilStr
  const isEvery = interval === 1

  let monthArr = bymonth
  if (bymonth && !Array.isArray(bymonth)) {
    monthArr = [bymonth]
  }

  let monthdayArr = bymonthday
  if (bymonthday && !Array.isArray(bymonthday)) {
    monthdayArr = [bymonthday]
  }

  const month = monthArr ? (monthArr as number[])[0] : null
  const day = monthdayArr ? (monthdayArr as number[])[0] : null

  if (!month || !day) return ''

  const dateStr = _formatMonthDay(month, day)

  if (isEvery && hasUntil) {
    return t('calendar.recurrence.yearlyDesc.until', {
      dateStr,
      until: untilStr,
    })
  }

  if (isEvery && !hasUntil) {
    if (!count || count === 0) {
      return t('calendar.recurrence.yearlyDesc.endless', {
        dateStr,
      })
    }
    if (count === 1) {
      return t('calendar.recurrence.yearlyDesc.count1', {
        dateStr,
      })
    }
    return t('calendar.recurrence.yearlyDesc.count', {
      dateStr,
      count,
    })
  }

  if (!isEvery && hasUntil) {
    return t('calendar.recurrence.yearlyDesc.intervalUntil', {
      interval,
      dateStr,
      untilStr,
    })
  }

  if (!isEvery && !hasUntil) {
    if (!count || count === 0) {
      return t('calendar.recurrence.yearlyDesc.intervalEndless', {
        interval,
        dateStr,
      })
    }
    if (count === 1) {
      return t('calendar.recurrence.yearlyDesc.intervalCount1', {
        interval,
        dateStr,
      })
    }
    return t('calendar.recurrence.yearlyDesc.intervalCount', {
      interval,
      dateStr,
      count,
    })
  }

  return ''
}

/**
 * Get localized month name
 */
const _getMonthName = (month: number | number[] | undefined): string => {
  if (!month) return ''

  let monthArr = month
  if (!Array.isArray(month)) {
    monthArr = [month]
  }

  if ((monthArr as number[]).length === 0) return ''

  return moment()
    .month((monthArr as number[])[0] - 1)
    .format('MMM')
}

/**
 * Generate description for yearly recurrence (by nth weekday of month)
 */
const _descYearlyNth = (
  interval: number,
  byweekday: ByWeekday[] | Weekday[] | number[] | undefined,
  bysetpos: number | number[] | undefined,
  bymonth: number | number[] | undefined,
  until?: Date,
  count?: number,
): string => {
  const untilStr = _formatUntilDate(until)
  const hasUntil = !!untilStr
  const isEvery = interval === 1

  const nth = _getNthText(bysetpos)
  const weekday = _nthWeekDayText(byweekday)
  const month = _getMonthName(bymonth)

  if (!nth || !weekday || !month) return ''

  if (isEvery && hasUntil) {
    return t('calendar.recurrence.yearlyNthDesc.until', {
      nth,
      weekday,
      month,
      until: untilStr,
    })
  }

  if (isEvery && !hasUntil) {
    if (!count || count === 0) {
      return t('calendar.recurrence.yearlyNthDesc.endless', {
        nth,
        weekday,
        month,
      })
    }
    if (count === 1) {
      return t('calendar.recurrence.yearlyNthDesc.count1', {
        nth,
        weekday,
        month,
      })
    }
    return t('calendar.recurrence.yearlyNthDesc.count', {
      nth,
      weekday,
      month,
      count,
    })
  }

  if (!isEvery && hasUntil) {
    return t('calendar.recurrence.yearlyNthDesc.intervalUntil', {
      interval,
      nth,
      weekday,
      month,
      untilStr,
    })
  }

  if (!isEvery && !hasUntil) {
    if (!count || count === 0) {
      return t('calendar.recurrence.yearlyNthDesc.intervalEndless', {
        interval,
        nth,
        weekday,
        month,
      })
    }
    if (count === 1) {
      return t('calendar.recurrence.yearlyNthDesc.intervalCount1', {
        interval,
        nth,
        weekday,
        month,
      })
    }
    return t('calendar.recurrence.yearlyNthDesc.intervalCount', {
      interval,
      nth,
      weekday,
      month,
      count,
    })
  }

  return ''
}
